import type { FC, ReactNode } from 'react'

import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { isPlaceholder, placeholderText } from '@/constants/profile'

const HINT = 'Placeholder. Replace it in src/constants/profile.ts'

interface EditableProps {
    value: string
    className?: string
}

interface EditableLinkProps {
    href: string
    label: string
    className?: string
    showArrow?: boolean
}

const PlaceholderChip: FC<{ children: ReactNode, className?: string }> = ({ children, className }): ReactNode => {
    return (
        <span
            title={HINT}
            className={cn(
                'inline-flex max-w-full items-baseline gap-1.5 rounded-sm border border-dashed border-border-strong bg-surface px-1.5 py-px align-baseline leading-snug',
                className
            )}
        >
            <span className='font-mono text-[0.625rem] font-medium uppercase not-italic tracking-label text-brand'>
                Edit
            </span>
            <span className='min-w-0 text-muted-foreground'>{children}</span>
        </span>
    )
}

/** Renders profile text, or a clearly marked chip while it is still a [[placeholder]]. */
const Editable: FC<EditableProps> = ({ value, className }): ReactNode => {
    if (!isPlaceholder(value)) return <span className={className}>{value}</span>

    return <PlaceholderChip className={className}>{placeholderText(value)}</PlaceholderChip>
}

/** A profile link that only becomes clickable once a real URL replaces the placeholder. */
export const EditableLink: FC<EditableLinkProps> = ({ href, label: rawLabel, className, showArrow = true }): ReactNode => {
    const label = placeholderText(rawLabel)

    if (isPlaceholder(href)) {
        return (
            <PlaceholderChip className={className}>
                {label}
                <span className='sr-only'>, link not added yet</span>
            </PlaceholderChip>
        )
    }

    const isMail = href.includes('@') && !href.startsWith('http')
    const url = isMail && !href.startsWith('mailto:') ? `mailto:${href}` : href

    return (
        <a
            href={url}
            {...(isMail ? {} : { target: '_blank', rel: 'noopener noreferrer me' })}
            className={cn('group/link inline-flex items-center gap-1.5 link-underline hover:text-brand', className)}
        >
            <span>{label}</span>
            {showArrow && !isMail && (
                <ArrowUpRight
                    aria-hidden='true'
                    className='size-3.5 transition-transform duration-300 ease-editorial group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5'
                />
            )}
        </a>
    )
}

export default Editable
